import { CalculatorInputs, VATType, RetentionType } from "../types";
import SectionCard from "../components/SectionCard";
import EffectiveValueBadge from "../components/EffectiveValueBadge";
import Accordion from "../components/ui/Accordion";
import { Input, Select, MoneyInput } from "../components/ui/Form";
import InfoCard from "../components/InfoCard";
import { getTarifaPlanaDetails } from "../utils/reta";
import { Lightbulb, AlertCircle, TrendingUp } from "lucide-react";

export default function InputForm({
  inputs,
  onChange,
  showEffective = false,
  incomeMul = 1,
  costMul = 1,
  showExampleNotice = false,
  isMobile = false
}: {
  inputs: CalculatorInputs;
  onChange: (i: CalculatorInputs) => void;
  showEffective?: boolean;
  incomeMul?: number;
  costMul?: number;
  showExampleNotice?: boolean;
  isMobile?: boolean;
}) {
  const update = (patch: Partial<CalculatorInputs>) => onChange({ ...inputs, ...patch });

  const newCostWithDefaults = (concept: string = "") => {
    let deductiblePct = 100;
    const l = concept.toLowerCase();
    if (l.includes("vivienda") || l.includes("suministro")) deductiblePct = 30;
    if (l.includes("veh") || l.includes("tel") || l.includes("móv")) deductiblePct = 50;
    return { concept, baseAmount: 0, hasVAT: true, vatType: "general_21" as const, deductiblePct };
  };

  const InfoHover = ({ content }: { content: string }) => (
    <div style={{ position: "relative", display: "inline-flex" }}>
      <button
        type="button"
        title={content}
        style={{
          width: "14px",
          height: "14px",
          borderRadius: "50%",
          border: "1px solid var(--muted)",
          background: "transparent",
          color: "var(--muted)",
          fontSize: "9px",
          fontWeight: 600,
          cursor: "help",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: 0,
          marginLeft: 6
        }}
        onMouseEnter={e => {
          const tooltip = e.currentTarget.nextElementSibling as HTMLElement;
          if (tooltip) tooltip.style.display = "block";
        }}
        onMouseLeave={e => {
          const tooltip = e.currentTarget.nextElementSibling as HTMLElement;
          if (tooltip) tooltip.style.display = "none";
        }}
      >
        i
      </button>
      <div
        style={{
          display: "none",
          position: "absolute",
          left: "50%",
          transform: "translateX(-50%)",
          top: "calc(100% + 6px)",
          background: "var(--card)",
          border: "1px solid var(--border)",
          borderRadius: "8px",
          padding: "10px",
          width: "240px",
          fontSize: "12px",
          lineHeight: "1.5",
          color: "var(--text)",
          boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
          zIndex: 1000,
          pointerEvents: "none"
        }}
      >
        {content}
      </div>
    </div>
  );

  const hasIncomeData = inputs.activities.length > 0;
  const hasCostsData =
    inputs.costs.length > 0 ||
    (inputs.housingAffectationPct ?? 0) > 0 ||
    (inputs.utilitiesAnnual ?? 0) > 0 ||
    (inputs.vehicleAffectationPct ?? 0) > 0 ||
    (inputs.vehicleCostsAnnual ?? 0) > 0 ||
    !!inputs.aplicaDFJ;
  const hasRetaData =
    !!inputs.isTarifaPlana ||
    (inputs.socialSecurityAnnual ?? 0) > 0 ||
    inputs.autoRETA === false;
  const hasDeduccionesData =
    (inputs.personal.healthInsuranceCoveredPersons ?? 0) > 0 ||
    (inputs.personal.healthInsuranceAnnual ?? 0) > 0 ||
    (inputs.personal.hijosCount ?? 0) > 0 ||
    (inputs.personal.hijosMenores3Count ?? 0) > 0 ||
    (inputs.personal.ascendientesCount ?? 0) > 0 ||
    (inputs.personal.dependientesDiscapacidadCount ?? 0) > 0 ||
    (inputs.personal.familiaNumerosa ?? inputs.personal.familyNumerous ?? "none") !== "none" ||
    (inputs.personal.pensionContribAnnual ?? 0) > 0 ||
    (inputs.personal.donationsAnnual ?? 0) > 0 ||
    (inputs.deduccionesAutonomicasManuales ?? 0) > 0;

  const tarifaPlana = getTarifaPlanaDetails(inputs.year);
  const fmtMoney = (value: number) =>
    new Intl.NumberFormat("es-ES", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value);
  const fmtPct = (value: number) =>
    new Intl.NumberFormat("es-ES", { minimumFractionDigits: 1, maximumFractionDigits: 1 }).format(value);
  const tarifaPlanaBreakdown = `Tarifa plana: ${fmtMoney(tarifaPlana.baseMonthly)} €/mes + MEI (${fmtPct(tarifaPlana.meiRate * 100)}%): ${fmtMoney(tarifaPlana.meiAmount)} €/mes = ${fmtMoney(tarifaPlana.totalMonthly)} €/mes`;
  const retaActiveMode = inputs.profile?.retaActiveMode ?? "full_year";
  const retaActiveDays = inputs.profile?.retaActiveDays ?? 0;
  const retaActiveDaysError = retaActiveMode === "days" && (!retaActiveDays || retaActiveDays < 1);
  const autoRetaMonthly = (inputs.socialSecurityAnnual ?? 0) / 12;
  const showMinRetaHint = !!inputs.autoRETA && !inputs.isTarifaPlana && !hasIncomeData && !hasCostsData;

  const exampleSections = [
    hasIncomeData ? "Ingresos" : null,
    hasCostsData ? "Costes" : null,
    hasRetaData ? "Cuota de autónomos" : null,
    hasDeduccionesData ? "Deducciones" : null
  ].filter(Boolean) as string[];
  const showExampleBanner = showExampleNotice && exampleSections.length > 0;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      {showExampleBanner && (
        <InfoCard
          icon={AlertCircle}
          variant="warning"
          title="Datos de ejemplo cargados"
          description={`Hay información en: ${exampleSections.join(", ")}. Revisa esos apartados antes de interpretar los resultados.`}
        />
      )}
      <Accordion title="Perfil" subtitle="Configura región y parámetros básicos" defaultOpen={isMobile}>
        <SectionCard>
        <div className="form-grid form-grid-3">
          <label>Año
            <select value={inputs.year} onChange={e => update({ year: Number(e.target.value) })}>
              <option value={2026}>2026 (por defecto)</option>
              <option value={2025}>2025</option>
            </select>
          </label>
          <label>
            <div style={{ display: "flex", alignItems: "baseline", gap: "6px" }}>
              <span>Región</span>
              <span style={{ fontSize: "11px", color: "var(--muted)", fontWeight: 400 }}>(afecta a los tramos de IRPF)</span>
            </div>
            <select value={inputs.region} onChange={e => update({ region: e.target.value as any })}>
              <option value="general">General (régimen común estatal)</option>
              <option value="andalucia">Andalucía</option>
              <option value="aragon">Aragón</option>
              <option value="asturias">Asturias</option>
              <option value="baleares">Illes Balears</option>
              <option value="canarias">Canarias</option>
              <option value="cantabria">Cantabria</option>
              <option value="castillalamancha">Castilla-La Mancha</option>
              <option value="castillayleon">Castilla y León</option>
              <option value="cataluna">Cataluña</option>
              <option value="extremadura">Extremadura</option>
              <option value="galicia">Galicia</option>
              <option value="rioja">La Rioja</option>
              <option value="madrid">Madrid</option>
              <option value="murcia">Murcia</option>
              <option value="valencia">Comunidad Valenciana</option>
            </select>
          </label>
        </div>
        <div style={{ marginTop: "16px", paddingTop: "16px", borderTop: "1px solid var(--border)" }}>
          <div style={{ fontSize: "12px", fontWeight: 700, color: "var(--muted)", marginBottom: "8px" }}>
            ¿Cuánto tiempo prevés estar de alta como autónomo este año?
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
            <label style={{ display: "flex", alignItems: "center", gap: "10px", cursor: "pointer" }}>
              <input
                type="radio"
                name="reta-active-mode"
                checked={retaActiveMode === "full_year"}
                onChange={() =>
                  update({
                    profile: {
                      ...(inputs.profile ?? { retaActiveMode: "full_year" }),
                      retaActiveMode: "full_year"
                    }
                  })
                }
              />
              <span style={{ fontSize: "13px", color: "var(--text)" }}>Todo el año</span>
            </label>
            <label style={{ display: "flex", alignItems: "center", gap: "10px", cursor: "pointer", flexWrap: "wrap" }}>
              <input
                type="radio"
                name="reta-active-mode"
                checked={retaActiveMode === "days"}
                onChange={() =>
                  update({
                    profile: {
                      ...(inputs.profile ?? { retaActiveMode: "days" }),
                      retaActiveMode: "days"
                    }
                  })
                }
              />
              <span style={{ fontSize: "13px", color: "var(--text)" }}>Un determinado tiempo</span>
              <input
                type="number"
                min={1}
                max={365}
                value={retaActiveMode === "days" ? retaActiveDays || "" : ""}
                onChange={e => {
                  const raw = Number(e.target.value || 0);
                  const clamped = Math.min(365, Math.max(0, isNaN(raw) ? 0 : raw));
                  update({
                    profile: {
                      ...(inputs.profile ?? { retaActiveMode: "days" }),
                      retaActiveMode: "days",
                      retaActiveDays: clamped
                    }
                  });
                }}
                disabled={retaActiveMode !== "days"}
                placeholder="1–365"
                style={{
                  width: "120px",
                  padding: "8px 10px",
                  border: `1px solid ${retaActiveDaysError ? "var(--danger)" : "var(--border)"}`,
                  borderRadius: "8px",
                  fontSize: "13px",
                  backgroundColor: retaActiveMode === "days" ? "white" : "#F3F4F6",
                  color: retaActiveMode === "days" ? "var(--text)" : "var(--muted)"
                }}
              />
            </label>
            {retaActiveDaysError && (
              <div className="err">Introduce un número de días (1–365)</div>
            )}
            <div style={{ fontSize: "11px", color: "var(--muted)" }}>
              Prorrateamos la cuota de autónomos anual según los días en alta.
            </div>
          </div>
        </div>
        </SectionCard>
      </Accordion>

      <Accordion
        title="Ingresos"
        subtitle="Añade líneas de actividad (IVA / retención)"
        forceOpen={hasIncomeData}
      >
        <InfoCard
          icon={Lightbulb}
          variant="tip"
          title="Introduce todas tus fuentes de ingresos"
          description="Recuerda que la base imponible es el importe SIN IVA. Si facturas 1.210€ con IVA al 21%, tu base es 1.000€. El tipo de retención depende de tu actividad: profesionales suelen tener 15%, actividades agrícolas 7%, alquileres 19%."
        />
        <SectionCard>
        {inputs.activities.map((a, idx) => (
          <div key={idx} style={{ marginBottom: "20px", padding: "16px", backgroundColor: "#f8f9fb", borderRadius: "12px", border: "1px solid #e5e7eb" }}>
            <div className="input-grid" style={{ alignItems: "end" }}>
              <div className="col-span-3">
                <label style={{ display: "block", fontSize: "0.75rem", fontWeight: 500, color: "#6b7280", marginBottom: "6px" }}>
                  Concepto
                </label>
                <Input
                  placeholder="Consultoría, docencia..."
                  value={a.concept}
                  onChange={e => {
                    const copy = [...inputs.activities]; copy[idx] = { ...a, concept: e.target.value }; update({ activities: copy });
                  }}
                />
              </div>
              <div className="col-span-4">
                <label style={{ display: "block", fontSize: "0.75rem", fontWeight: 500, color: "#6b7280", marginBottom: "6px" }}>
                  Base Imponible
                </label>
                <div style={{ position: "relative" }}>
                  <MoneyInput
                    value={a.baseAmount}
                    onChange={n => {
                      const copy = [...inputs.activities]; copy[idx] = { ...a, baseAmount: n }; update({ activities: copy });
                    }}
                    placeholder="0"
                    className={showEffective && incomeMul !== 1 ? "has-badge" : ""}
                    frequency={a.frequency || 'annual'}
                    onFrequencyChange={freq => {
                      const copy = [...inputs.activities]; copy[idx] = { ...a, frequency: freq }; update({ activities: copy });
                    }}
                  />
                  {showEffective && <EffectiveValueBadge base={a.baseAmount} multiplier={incomeMul ?? 1} align="right" />}
                </div>
              </div>
              <div className="col-span-2">
                <label style={{ display: "block", fontSize: "0.75rem", fontWeight: 500, color: "#6b7280", marginBottom: "6px" }}>
                  Tipo de IVA
                </label>
                <Select value={a.vatType} onChange={e => { const copy=[...inputs.activities]; copy[idx]={...a, vatType: e.target.value as VATType}; update({activities: copy});}}>
                  <option value="general_21">IVA 21%</option>
                  <option value="reduced_10">IVA 10%</option>
                  <option value="superreduced_4">IVA 4%</option>
                  <option value="exempt">Exento</option>
                  <option value="reverse_charge_eu">Inv. suj. pasivo</option>
                </Select>
              </div>
              <div className="col-span-2">
                <label style={{ display: "block", fontSize: "0.75rem", fontWeight: 500, color: "#6b7280", marginBottom: "6px" }}>
                  Retención IRPF
                </label>
                <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                  <Select
                    value={a.retenType}
                    onChange={e => {
                      const copy=[...inputs.activities];
                      const newType = e.target.value as RetentionType;
                      copy[idx]={...a, retenType: newType, customRetentionPct: newType === "custom" ? (a.customRetentionPct ?? 19) : undefined};
                      update({activities: copy});
                    }}
                  >
                    <option value="none">Sin retención</option>
                    <option value="profesional_7">7%</option>
                    <option value="profesional_15">15%</option>
                    <option value="rent_19">19%</option>
                    <option value="custom">Otro %</option>
                  </Select>
                  {a.retenType === "custom" && (
                    <div style={{ position: "relative", display: "flex", alignItems: "center" }}>
                      <input
                        type="number"
                        min={0}
                        max={100}
                        step={0.1}
                        value={a.customRetentionPct ?? ""}
                        onChange={e => {
                          const pct = Math.max(0, Math.min(100, Number(e.target.value || 0)));
                          const copy=[...inputs.activities];
                          copy[idx]={...a, customRetentionPct: pct};
                          update({activities: copy});
                        }}
                        placeholder="ej. 19"
                        style={{
                          width: "100%",
                          padding: "8px 32px 8px 10px",
                          border: "1px solid var(--border)",
                          borderRadius: "8px",
                          fontSize: "13px"
                        }}
                      />
                      <span style={{
                        position: "absolute",
                        right: "10px",
                        color: "var(--muted)",
                        pointerEvents: "none",
                        fontSize: "12px"
                      }}>%</span>
                    </div>
                  )}
                </div>
              </div>
              <div className="col-span-1" style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                <button className="button icon danger" onClick={() => { const copy=[...inputs.activities]; copy.splice(idx,1); update({activities: copy});}}>
                  Eliminar
                </button>
              </div>
            </div>
          </div>
        ))}
        <button className="button brand full-width-mobile" onClick={() => update({ activities: [...inputs.activities, { concept: "", baseAmount: 0, vatType: "general_21", retenType: "profesional_15" }] })}>
          + Añadir ingreso
        </button>
        </SectionCard>
      </Accordion>

      <Accordion
        title="Costes"
        subtitle="Introduce gastos y % deducible"
        forceOpen={hasCostsData}
      >
        <InfoCard
          icon={TrendingUp}
          variant="success"
          title="Los gastos deducibles reducen tu IRPF"
          description="Solo puedes deducir gastos relacionados con tu actividad profesional. Guarda todas las facturas. Los gastos mixtos (como el teléfono o vehículo) solo se deducen parcialmente según el uso profesional."
        />
        <SectionCard>
        {inputs.costs.map((c, idx) => {
          const isCustom = c.deductiblePct === -1;
          const selectValue = isCustom ? -1 : (c.deductiblePct ?? 100);

          return (
            <div key={idx} style={{ marginBottom: "20px", padding: "16px", backgroundColor: "#f8f9fb", borderRadius: "12px", border: "1px solid #e5e7eb" }}>
              <div className="input-grid" style={{ alignItems: "end" }}>
                <div className="col-span-3">
                  <label style={{ display: "block", fontSize: "0.75rem", fontWeight: 500, color: "#6b7280", marginBottom: "6px" }}>
                    Concepto
                  </label>
                  <Input
                    placeholder="Coworking, software..."
                    value={c.concept}
                    onChange={e => { const copy=[...inputs.costs]; copy[idx]={...c, concept: e.target.value}; update({costs: copy});}}
                  />
                </div>
                <div className="col-span-4">
                  <label style={{ display: "block", fontSize: "0.75rem", fontWeight: 500, color: "#6b7280", marginBottom: "6px" }}>
                    Importe
                  </label>
                  <div style={{ position: "relative" }}>
                    <MoneyInput
                      value={c.baseAmount}
                      onChange={n => { const copy=[...inputs.costs]; copy[idx]={...c, baseAmount: n}; update({costs: copy});}}
                      placeholder="0"
                      className={showEffective && costMul !== 1 ? "has-badge" : ""}
                      frequency={c.frequency || 'annual'}
                      onFrequencyChange={freq => {
                        const copy = [...inputs.costs]; copy[idx] = { ...c, frequency: freq }; update({ costs: copy });
                      }}
                    />
                    {showEffective && <EffectiveValueBadge base={c.baseAmount} multiplier={costMul ?? 1} align="right" />}
                  </div>
                </div>
                <div className="col-span-2">
                  <label style={{ display: "block", fontSize: "0.75rem", fontWeight: 500, color: "#6b7280", marginBottom: "6px" }}>
                    Tipo de IVA
                  </label>
                  <Select
                    value={c.hasVAT ? (c.vatType ?? "general_21") : "exempt"}
                    onChange={e => {
                      const copy=[...inputs.costs];
                      const val = e.target.value;
                      if (val === "exempt") {
                        copy[idx]={...c, hasVAT: false, vatType: undefined};
                      } else {
                        copy[idx]={...c, hasVAT: true, vatType: val as VATType};
                      }
                      update({costs: copy});
                    }}
                  >
                    <option value="general_21">IVA 21%</option>
                    <option value="reduced_10">IVA 10%</option>
                    <option value="superreduced_4">IVA 4%</option>
                    <option value="exempt">Sin IVA</option>
                  </Select>
                </div>
                <div className="col-span-2">
                  <label style={{ display: "block", fontSize: "0.75rem", fontWeight: 500, color: "#6b7280", marginBottom: "6px" }}>
                    Deducción del gasto
                  </label>
                  <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                    <select
                      value={selectValue}
                      onChange={e => {
                        const val = Number(e.target.value);
                        const copy = [...inputs.costs];
                        if (val === -1) {
                          copy[idx] = { ...c, deductiblePct: -1, customDeductible: c.customDeductible ?? 100 };
                        } else {
                          copy[idx] = { ...c, deductiblePct: val, customDeductible: undefined };
                        }
                        update({ costs: copy });
                      }}
                      className="w-full appearance-none rounded-xl border border-slate-300 bg-white px-3 py-2 pr-8 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="100">100% — Totalmente profesional</option>
                      <option value="50">50% — Uso mixto (vehículo/teléfono)</option>
                      <option value="30">30% — Vivienda (suministros afectados)</option>
                      <option value="0">0% — No deducible</option>
                      <option value="-1">Personalizado…</option>
                    </select>

                    {isCustom && (
                      <div className="relative">
                        <input
                          type="number"
                          min={0}
                          max={100}
                          value={c.customDeductible ?? ""}
                          onChange={e => {
                            const n = Math.max(0, Math.min(100, Number(e.target.value || 0)));
                            const copy = [...inputs.costs];
                            copy[idx] = { ...c, customDeductible: n };
                            update({ costs: copy });
                          }}
                          className="w-28 rounded-xl border border-slate-300 bg-white px-3 py-2 pr-9 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="ej. 75"
                        />
                        <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-slate-500">%</span>
                      </div>
                    )}
                  </div>
                </div>
                <div className="col-span-1" style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <button className="button icon danger" onClick={() => { const copy=[...inputs.costs]; copy.splice(idx,1); update({costs: copy});}}>
                    Eliminar
                  </button>
                </div>
              </div>
            </div>
          );
        })}
        <button className="button brand full-width-mobile" onClick={() => update({ costs: [...inputs.costs, newCostWithDefaults()] })}>
          + Añadir coste
        </button>
        </SectionCard>

        <SectionCard style={{ marginTop: 8 }}>
        <div className="form-grid form-grid-4">
          <label>% Vivienda afecta <InfoHover content="Porcentaje de vivienda habitual afecta a la actividad. AEAT: los suministros solo deducen el 30% de esa parte." />
            <input
              type="number"
              min={0}
              max={100}
              value={inputs.housingAffectationPct || ""}
              onChange={e => {
                const v = +e.target.value;
                const clamped = Math.min(100, Math.max(0, isNaN(v) ? 0 : v));
                update({ housingAffectationPct: clamped });
              }}
              placeholder="0"
            />
          </label>
          <label>Suministros vivienda <InfoHover content="Agua, luz, gas, internet… Deducción: 30% de estos gastos por el porcentaje de vivienda afecta declarado." />
            <div style={{ position: "relative", display: "flex", alignItems: "center" }}>
              <input type="number" value={inputs.utilitiesAnnual || ""} onChange={e => update({ utilitiesAnnual: +e.target.value || 0 })} style={{ paddingRight: "28px" }} placeholder="0"/>
              <span style={{ position: "absolute", right: "12px", color: "var(--muted)", pointerEvents: "none", fontSize: "14px" }}>€</span>
            </div>
          </label>
          <label>% Vehículo afecta <InfoHover content="IRPF: solo se admite deducción de gastos de vehículo con afectación exclusiva (100%) a la actividad." />
            <select
              value={inputs.vehicleAffectationPct ?? 0}
              onChange={e => {
                const v = Number(e.target.value) === 100 ? 100 : 0;
                update({ vehicleAffectationPct: v });
              }}
            >
              <option value={0}>0% — Uso compartido (sin deducción)</option>
              <option value={100}>100% — Afectación exclusiva (deducible)</option>
            </select>
          </label>
          <label>Costes vehículo <InfoHover content="Solo deducibles si el vehículo está íntegramente afecto (criterio AEAT): combustible, mantenimiento, seguro, etc." />
            <div style={{ position: "relative", display: "flex", alignItems: "center" }}>
              <input type="number" value={inputs.vehicleCostsAnnual || ""} onChange={e => update({ vehicleCostsAnnual: +e.target.value || 0 })} style={{ paddingRight: "28px" }} placeholder="0"/>
              <span style={{ position: "absolute", right: "12px", color: "var(--muted)", pointerEvents: "none", fontSize: "14px" }}>€</span>
            </div>
          </label>
        </div>
        </SectionCard>

        <SectionCard style={{ marginTop: 8 }}>
          <div style={{
            padding: "16px",
            backgroundColor: "rgba(59, 130, 246, 0.05)",
            borderRadius: "8px",
            border: "1px solid rgba(59, 130, 246, 0.15)"
          }}>
            <label style={{
              display: "flex",
              alignItems: "flex-start",
              gap: "12px",
              cursor: "pointer"
            }}>
              <input
                type="checkbox"
                checked={!!inputs.aplicaDFJ}
                onChange={e => update({ aplicaDFJ: e.target.checked })}
                style={{
                  width: "18px",
                  height: "18px",
                  marginTop: "2px",
                  cursor: "pointer"
                }}
              />
              <div style={{ flex: 1 }}>
                <span style={{ fontSize: "13px", fontWeight: 600, color: "var(--text)", display: "block", marginBottom: "4px" }}>
                  Aplicar Gastos de Difícil Justificación
                </span>
                <span style={{ fontSize: "11px", color: "var(--muted)", lineHeight: "1.4" }}>
                  Aplica la reducción del 5% sobre tu rendimiento neto, con un máximo de 2.000 €
                </span>
              </div>
            </label>
          </div>
        </SectionCard>

      </Accordion>

      <Accordion
        title="Cuota de autónomos"
        subtitle="Cuota, tarifa plana y cálculo automático"
        defaultOpen={false}
        forceOpen={hasRetaData}
      >
        <SectionCard>
          <div style={{
            padding: "16px",
            backgroundColor: "#F9FAFB",
            borderRadius: "8px",
            border: "1px solid #E5E7EB"
          }}>
            <div style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              marginBottom: "12px"
            }}>
              <h4 style={{
                margin: 0,
                fontSize: "14px",
                fontWeight: 600,
                color: "var(--text)"
              }}>
                Cuota de autónomos (RETA)
              </h4>
              <div style={{ position: "relative", display: "inline-flex" }}>
                <button
                  type="button"
                  title="Información sobre cuota de autónomos"
                  style={{
                    width: "18px",
                    height: "18px",
                    borderRadius: "50%",
                    border: "1.5px solid var(--muted)",
                    background: "transparent",
                    color: "var(--muted)",
                    fontSize: "11px",
                    fontWeight: 700,
                    cursor: "help",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: 0
                  }}
                  onMouseEnter={e => {
                    const tooltip = e.currentTarget.nextElementSibling as HTMLElement;
                    if (tooltip) tooltip.style.display = "block";
                  }}
                  onMouseLeave={e => {
                    const tooltip = e.currentTarget.nextElementSibling as HTMLElement;
                    if (tooltip) tooltip.style.display = "none";
                  }}
                >
                  i
                </button>
                <div style={{
                  display: "none",
                  position: "absolute",
                  left: "50%",
                  transform: "translateX(-50%)",
                  top: "calc(100% + 8px)",
                  background: "var(--card)",
                  border: "1px solid var(--border)",
                  borderRadius: "8px",
                  padding: "12px",
                  width: "300px",
                  fontSize: "12px",
                  lineHeight: "1.5",
                  color: "var(--text)",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                  zIndex: 1000,
                  pointerEvents: "none"
                }}>
                  <div style={{ marginBottom: "8px" }}>
                    La cuota de autónomos (RETA) es un <strong>gasto deducible</strong>.
                  </div>
                  <div style={{ marginBottom: "8px" }}>
                    En el cálculo automático se usa la base de cotización actualizada a 2025.
                  </div>
                  <div>
                    Si aplicas bonificaciones (Tarifa Plana, cuotas reducidas, etc.),
                    puedes introducir manualmente el importe anual.
                  </div>
                </div>
              </div>
            </div>

            <label style={{
              display: "flex",
              alignItems: "flex-start",
              gap: "10px",
              marginBottom: "12px",
              cursor: "pointer"
            }}>
              <input
                type="checkbox"
                checked={!!inputs.isTarifaPlana}
                onChange={e => {
                  const checked = e.target.checked;
                  update({
                    isTarifaPlana: checked,
                    socialSecurityAnnual: checked ? tarifaPlana.totalAnnual : inputs.socialSecurityAnnual
                  });
                }}
                style={{
                  width: "18px",
                  height: "18px",
                  marginTop: "2px",
                  cursor: "pointer"
                }}
              />
              <div style={{ flex: 1 }}>
                <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                  <span style={{ fontSize: "13px", fontWeight: 600, color: "var(--text)" }}>
                    Aplicar Tarifa Plana (Nuevos autónomos)
                  </span>
                  <div style={{ position: "relative", display: "inline-flex" }}>
                    <button
                      type="button"
                      title="Información sobre Tarifa Plana"
                      style={{
                        width: "18px",
                        height: "18px",
                        borderRadius: "50%",
                        border: "1.5px solid var(--muted)",
                        background: "transparent",
                        color: "var(--muted)",
                        fontSize: "11px",
                        fontWeight: 700,
                        cursor: "help",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        padding: 0
                      }}
                      onMouseEnter={e => {
                        const tooltip = e.currentTarget.nextElementSibling as HTMLElement;
                        if (tooltip) tooltip.style.display = "block";
                      }}
                      onMouseLeave={e => {
                        const tooltip = e.currentTarget.nextElementSibling as HTMLElement;
                        if (tooltip) tooltip.style.display = "none";
                      }}
                    >
                      i
                    </button>
                    <div style={{
                      display: "none",
                      position: "absolute",
                      left: "50%",
                      transform: "translateX(-50%)",
                      top: "calc(100% + 8px)",
                      background: "var(--card)",
                      border: "1px solid var(--border)",
                      borderRadius: "8px",
                      padding: "12px",
                      width: "340px",
                      fontSize: "12px",
                      lineHeight: "1.5",
                      color: "var(--text)",
                      boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                      zIndex: 1000,
                    pointerEvents: "none"
                  }}>
                    <div style={{ marginBottom: "8px" }}>
                      <strong>{tarifaPlanaBreakdown}.</strong>
                    </div>
                    <div style={{ marginBottom: "8px" }}>
                      <strong>Requisitos:</strong> No haber estado de alta como autónomo en los últimos 2 años (o 3 si ya disfrutaste de bonificación anteriormente).
                    </div>
                      <div>
                        En algunas CCAA (Madrid, Andalucía, etc.) esta cuota se subvenciona al 100% (Cuota Cero) posteriormente.
                      </div>
                    </div>
                  </div>
                </div>
                <span style={{ fontSize: "11px", color: "var(--muted)", lineHeight: "1.4", display: "block", marginTop: "4px" }}>
                  {tarifaPlanaBreakdown}. Total anual: {fmtMoney(tarifaPlana.totalAnnual)} €.
                </span>
              </div>
            </label>

            <label style={{
              display: "flex",
              alignItems: "center",
              gap: "10px",
              marginBottom: "12px",
              cursor: "pointer",
              opacity: inputs.isTarifaPlana ? 0.5 : 1
            }}>
              <input
                type="checkbox"
                checked={!!inputs.autoRETA}
                onChange={e => update({ autoRETA: e.target.checked })}
                disabled={!!inputs.isTarifaPlana}
                style={{
                  width: "18px",
                  height: "18px",
                  cursor: inputs.isTarifaPlana ? "not-allowed" : "pointer"
                }}
              />
              <span style={{ fontSize: "13px", fontWeight: 500, color: "var(--text)" }}>
                Calcular cuota automáticamente según base de cotización
              </span>
            </label>

            <div style={{ marginBottom: "12px" }}>
              <label style={{
                display: "block",
                fontSize: "12px",
                fontWeight: 600,
                color: "var(--text)",
                marginBottom: "6px"
              }}>
                Cuota anual (€)
              </label>
              <div style={{ position: "relative", display: "flex", alignItems: "center" }}>
                <input
                  type="number"
                  value={inputs.isTarifaPlana ? tarifaPlana.totalAnnual : (inputs.socialSecurityAnnual ?? "")}
                  onChange={e => update({ socialSecurityAnnual: Number(e.target.value || 0) })}
                  disabled={!!inputs.autoRETA || !!inputs.isTarifaPlana}
                  placeholder={`p.ej. ${fmtMoney(tarifaPlana.totalAnnual)} si tienes tarifa plana`}
                  style={{
                    width: "100%",
                    padding: "10px 12px",
                    paddingRight: "32px",
                    border: "1px solid var(--border)",
                    borderRadius: "8px",
                    fontSize: "14px",
                    backgroundColor: (inputs.autoRETA || inputs.isTarifaPlana) ? "#F3F4F6" : "white",
                    cursor: (inputs.autoRETA || inputs.isTarifaPlana) ? "not-allowed" : "text",
                    opacity: (inputs.autoRETA || inputs.isTarifaPlana) ? 0.6 : 1
                  }}
                />
                <span style={{
                  position: "absolute",
                  right: "12px",
                  color: "var(--muted)",
                  pointerEvents: "none",
                  fontSize: "13px",
                  fontWeight: 600
                }}>€</span>
              </div>
            </div>

            <div style={{
              fontSize: "11px",
              lineHeight: "1.5",
              color: "var(--muted)",
              padding: "10px",
              backgroundColor: "rgba(107, 114, 128, 0.05)",
              borderRadius: "6px"
            }}>
              La cuota de autónomos se calcula según la base de cotización estimada.
              Puedes modificarla si tienes bonificación (Tarifa Plana, reducciones, etc.).
              Si desmarcas el cálculo automático, podrás introducir tu cuota anual manualmente.
            </div>
            {showMinRetaHint && autoRetaMonthly > 0 && (
              <div style={{ marginTop: "8px", fontSize: "11px", color: "var(--muted)" }}>
                Corresponde a la cuota mínima ({fmtMoney(autoRetaMonthly)} €/mes).
              </div>
            )}
          </div>
        </SectionCard>
      </Accordion>

      <Accordion
        title="Deducciones"
        subtitle="Hijos, ascendientes y otros parámetros fiscales"
        defaultOpen={false}
        forceOpen={hasDeduccionesData}
      >
        <InfoCard
          icon={AlertCircle}
          variant="info"
          title="Tu situación personal reduce el IRPF que pagas"
          description="Hijos, ascendientes a cargo, familia numerosa, planes de pensiones y seguros médicos aumentan tus deducciones fiscales. Completa esta sección para calcular tu cuota real de IRPF."
        />
        <SectionCard>
          {/* DATOS PERSONALES */}
          <div style={{ marginBottom: "32px" }}>
            <div style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              marginBottom: "16px"
            }}>
              <h3 style={{
                margin: 0,
                fontSize: "15px",
                fontWeight: 600,
                color: "var(--text)",
                letterSpacing: "-0.01em"
              }}>
                Seguro médico
              </h3>
              <div style={{ position: "relative", display: "inline-flex" }}>
                <button
                  type="button"
                  title="Información sobre seguro médico"
                  style={{
                    width: "18px",
                    height: "18px",
                    borderRadius: "50%",
                    border: "1.5px solid var(--muted)",
                    background: "transparent",
                    color: "var(--muted)",
                    fontSize: "11px",
                    fontWeight: 700,
                    cursor: "help",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: 0
                  }}
                  onMouseEnter={e => {
                    const tooltip = e.currentTarget.nextElementSibling as HTMLElement;
                    if (tooltip) tooltip.style.display = "block";
                  }}
                  onMouseLeave={e => {
                    const tooltip = e.currentTarget.nextElementSibling as HTMLElement;
                    if (tooltip) tooltip.style.display = "none";
                  }}
                >
                  i
                </button>
                <div style={{
                  display: "none",
                  position: "absolute",
                  left: "50%",
                  transform: "translateX(-50%)",
                  top: "calc(100% + 8px)",
                  background: "var(--card)",
                  border: "1px solid var(--border)",
                  borderRadius: "8px",
                  padding: "12px",
                  width: "320px",
                  fontSize: "12px",
                  lineHeight: "1.5",
                  color: "var(--text)",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                  zIndex: 1000,
                  pointerEvents: "none"
                }}>
                  <strong style={{ display: "block", marginBottom: "6px" }}>
                    Se consideran las primas de seguros de salud pagadas por el autónomo para:
                  </strong>
                  <ul style={{ margin: "6px 0", paddingLeft: "18px" }}>
                    <li>Él/ella mismo/a</li>
                    <li>Su cónyuge</li>
                    <li>Hijos menores de 25 años que convivan</li>
                  </ul>
                  <div style={{ marginTop: "8px", paddingTop: "8px", borderTop: "1px solid var(--border)", fontSize: "11px" }}>
                    Introduce el total anual de la póliza y el número de personas cubiertas.
                    La deducción máxima es de <strong>500 € por persona y año</strong> (ej: 1.500 € si incluye cónyuge e hijos),
                    y todos tienen el mismo tratamiento fiscal.
                  </div>
                </div>
              </div>
            </div>

            <div className="form-grid form-grid-auto" style={{ marginBottom: "12px" }}>
              <label style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                <span style={{ fontSize: "13px", fontWeight: 600, color: "var(--text)" }}>
                  Personas cubiertas
                </span>
                <input
                  type="number"
                  min="0"
                  value={inputs.personal.healthInsuranceCoveredPersons || ""}
                  onChange={e => update({ personal: { ...inputs.personal, healthInsuranceCoveredPersons: +e.target.value || 0 }})}
                  style={{
                    padding: "10px 12px",
                    border: "1px solid var(--border)",
                    borderRadius: "8px",
                    fontSize: "14px"
                  }}
                  placeholder="0"
                />
              </label>

              <label style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                <span style={{ fontSize: "13px", fontWeight: 600, color: "var(--text)" }}>
                  Prima anual total
                </span>
                <div style={{ position: "relative", display: "flex", alignItems: "center" }}>
                  <input
                    type="number"
                    min="0"
                    value={inputs.personal.healthInsuranceAnnual || ""}
                    onChange={e => update({ personal: { ...inputs.personal, healthInsuranceAnnual: +e.target.value || 0 }})}
                    style={{
                      padding: "10px 12px",
                      paddingRight: "32px",
                      border: "1px solid var(--border)",
                      borderRadius: "8px",
                      fontSize: "14px",
                      width: "100%"
                    }}
                    placeholder="0"
                  />
                  <span style={{
                    position: "absolute",
                    right: "12px",
                    color: "var(--muted)",
                    pointerEvents: "none",
                    fontSize: "13px",
                    fontWeight: 600
                  }}>€</span>
                </div>
              </label>
            </div>

            <div style={{
              fontSize: "11px",
              lineHeight: "1.5",
              color: "var(--muted)",
              padding: "10px 12px",
              backgroundColor: "rgba(16, 185, 129, 0.05)",
              borderRadius: "6px",
              border: "1px solid rgba(16, 185, 129, 0.15)"
            }}>
              Introduce la prima anual total del seguro de salud que paga el autónomo
              (incluyéndose a sí mismo) y las personas que cubre en la misma póliza
              (cónyuge, hijos, etc.).
              Fiscalmente se puede deducir hasta <strong>500 € por persona cubierta y año</strong>
              (autónomo y familiares), con el mismo tratamiento para todos.
            </div>
          </div>

          {/* FAMILIA Y MÍNIMOS PERSONALES */}
          <div style={{ paddingTop: "32px", borderTop: "1px solid var(--border)" }}>
            <h3 style={{
              margin: "0 0 16px",
              fontSize: "15px",
              fontWeight: 600,
              color: "var(--text)",
              letterSpacing: "-0.01em"
            }}>
              Familia y mínimos personales
            </h3>
            <div style={{ fontSize: "12px", color: "var(--muted)", marginBottom: "16px" }}>
              Estos datos incrementan el mínimo personal y familiar y reducen la cuota de IRPF
            </div>
            <div className="form-grid form-grid-auto">
              <label style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                <span style={{ fontSize: "13px", fontWeight: 600, color: "var(--text)" }}>
                  Número de hijos ≤ 25 años
                </span>
                <input
                  type="number"
                  min={0}
                  step={1}
                  value={inputs.personal.hijosCount || ""}
                  onChange={e => {
                    const newHijosCount = Math.max(0, +e.target.value||0);
                    const currentMenores3 = inputs.personal.hijosMenores3Count ?? 0;
                    update({
                      personal: {
                        ...inputs.personal,
                        hijosCount: newHijosCount,
                        hijosMenores3Count: Math.min(currentMenores3, newHijosCount)
                      }
                    });
                  }}
                  style={{
                    padding: "10px 12px",
                    border: "1px solid var(--border)",
                    borderRadius: "8px",
                    fontSize: "14px"
                  }}
                  placeholder="0"
                />
              </label>

              <label style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                <span style={{ fontSize: "13px", fontWeight: 600, color: "var(--text)" }}>
                  De ellos, menores de 3 años
                </span>
                <input
                  type="number"
                  min={0}
                  step={1}
                  value={inputs.personal.hijosMenores3Count || ""}
                  onChange={e => {
                    const newMenores3 = Math.max(0, +e.target.value||0);
                    const hijosCount = inputs.personal.hijosCount ?? 0;
                    update({
                      personal: {
                        ...inputs.personal,
                        hijosMenores3Count: Math.min(newMenores3, hijosCount)
                      }
                    });
                  }}
                  style={{
                    padding: "10px 12px",
                    border: "1px solid var(--border)",
                    borderRadius: "8px",
                    fontSize: "14px"
                  }}
                  placeholder="0"
                />
              </label>

              <label style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                <span style={{ fontSize: "13px", fontWeight: 600, color: "var(--text)" }}>
                  Ascendientes dependientes
                </span>
                <input
                  type="number"
                  min={0}
                  step={1}
                  value={inputs.personal.ascendientesCount || ""}
                  onChange={e => update({ personal: { ...inputs.personal, ascendientesCount: Math.max(0, +e.target.value||0) }})}
                  style={{
                    padding: "10px 12px",
                    border: "1px solid var(--border)",
                    borderRadius: "8px",
                    fontSize: "14px"
                  }}
                  placeholder="0"
                />
              </label>

              <label style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                <span style={{ fontSize: "13px", fontWeight: 600, color: "var(--text)" }}>
                  Dependientes con discapacidad
                </span>
                <input
                  type="number"
                  min={0}
                  step={1}
                  value={inputs.personal.dependientesDiscapacidadCount || ""}
                  onChange={e => update({ personal: { ...inputs.personal, dependientesDiscapacidadCount: Math.max(0, +e.target.value||0) }})}
                  style={{
                    padding: "10px 12px",
                    border: "1px solid var(--border)",
                    borderRadius: "8px",
                    fontSize: "14px"
                  }}
                  placeholder="0"
                />
              </label>

              <label style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                <span style={{ fontSize: "13px", fontWeight: 600, color: "var(--text)" }}>
                  Familia numerosa
                </span>
                <select
                  value={inputs.personal.familiaNumerosa ?? "none"}
                  onChange={e => update({ personal: { ...inputs.personal, familiaNumerosa: e.target.value as any }})}
                  style={{
                    padding: "10px 12px",
                    border: "1px solid var(--border)",
                    borderRadius: "8px",
                    fontSize: "14px"
                  }}
                >
                  <option value="none">No</option>
                  <option value="general">General (1.200 €)</option>
                  <option value="especial">Especial (2.400 €)</option>
                </select>
              </label>
            </div>
          </div>

          {/* APORTACIONES Y DEDUCCIONES */}
          <div style={{ marginBottom: "32px", paddingTop: "32px", borderTop: "1px solid var(--border)" }}>
            <h3 style={{
              margin: "0 0 16px",
              fontSize: "15px",
              fontWeight: 600,
              color: "var(--text)",
              letterSpacing: "-0.01em"
            }}>
              Aportaciones y deducciones
            </h3>
            <div className="form-grid form-grid-auto">
              <label style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                <span style={{ fontSize: "13px", fontWeight: 600, color: "var(--text)" }}>
                  Planes de pensiones anuales
                </span>
                <div style={{ position: "relative", display: "flex", alignItems: "center" }}>
                  <input
                    type="number"
                    value={inputs.personal.pensionContribAnnual || ""}
                    onChange={e => update({ personal: { ...inputs.personal, pensionContribAnnual: +e.target.value || 0 }})}
                    style={{
                      padding: "10px 12px",
                      paddingRight: "32px",
                      border: "1px solid var(--border)",
                      borderRadius: "8px",
                      fontSize: "14px",
                      width: "100%"
                    }}
                    placeholder="0"
                  />
                  <span style={{
                    position: "absolute",
                    right: "12px",
                    color: "var(--muted)",
                    pointerEvents: "none",
                    fontSize: "13px",
                    fontWeight: 600
                  }}>€</span>
                </div>
                <span style={{ fontSize: "11px", color: "var(--muted)", lineHeight: "1.4" }}>
                  Se resta de la base imponible general hasta los límites fiscales habituales
                </span>
              </label>

              <label style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                <span style={{ fontSize: "13px", fontWeight: 600, color: "var(--text)" }}>
                  Donativos anuales
                </span>
                <div style={{ position: "relative", display: "flex", alignItems: "center" }}>
                  <input
                    type="number"
                    value={inputs.personal.donationsAnnual || ""}
                    onChange={e => update({ personal: { ...inputs.personal, donationsAnnual: +e.target.value || 0 }})}
                    style={{
                      padding: "10px 12px",
                      paddingRight: "32px",
                      border: "1px solid var(--border)",
                      borderRadius: "8px",
                      fontSize: "14px",
                      width: "100%"
                    }}
                    placeholder="0"
                  />
                  <span style={{
                    position: "absolute",
                    right: "12px",
                    color: "var(--muted)",
                    pointerEvents: "none",
                    fontSize: "13px",
                    fontWeight: 600
                  }}>€</span>
                </div>
                <span style={{ fontSize: "11px", color: "var(--muted)", lineHeight: "1.4" }}>
                  Se aplica una deducción simplificada (20%) sobre la cuota de IRPF
                </span>
              </label>

              <label style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                <span style={{ fontSize: "13px", fontWeight: 600, color: "var(--text)" }}>
                  Otras deducciones autonómicas
                </span>
                <MoneyInput
                  value={inputs.deduccionesAutonomicasManuales ?? 0}
                  onChange={n => update({ deduccionesAutonomicasManuales: n })}
                  placeholder="0"
                  suffix="€"
                />
                <span style={{ fontSize: "11px", color: "var(--muted)", lineHeight: "1.4" }}>
                  Si conoces deducciones específicas de tu Comunidad (alquiler, guardería, inversión...), introduce el importe total aquí.
                </span>
              </label>
            </div>
          </div>
        </SectionCard>
      </Accordion>
    </div>
  );
}
